/* eslint-disable no-nested-ternary */
// 🔩 Base
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

// 🔠 i18n
import { LocalizedMessage, LocalizedStandardDateNoSeconds } from 'intl/LocalizedComponents';

// 🔧 Utils
import _get from 'lodash/get';
import { dateGenerator } from 'utils/DateUtils';
import { formatAddressForCpmClient } from 'utils/Tools';

// 🧱 Components
import Box from 'components/core/Box';
import Card from 'components/core/Card';
import Typography from 'components/core/Typography';
import PurchaseInfoPrice from 'components/finance/PurchaseInfoPrice';

// Models
import mFinance from 'models/cpm/finance';

// 🫔 HOC
import { withTheme } from '@material-ui/core/styles';

// 🗜️ Types
import PropTypes from 'prop-types';
import {
  cpmMyBookingShape,
  cpmOnDemandAccessForCheckoutDataShape,
  cpmPurchaseShape,
  cpmSlugCustomizationShape,
} from 'constants/PropTypesShapes';

// Assets 🎨
import AppIcon from 'components/icons/AppIcon';
import classNames from 'classnames';

// 🪝 Hooks
import useResponsive from 'hooks/useResponsive';

const PurchaseWrapperBooking = ({
  product,
  purchase,
  children,
  customization,
  theme,
}) => {
  const { begin, vehicles } = product;
  let { end } = product;

  const { carpark: { settings: { general: { address: carparkAddress } } } } = product;
  const { carpark: { name: carparkName } } = product;

  const isMobile = useResponsive();
  const hasLogo = customization && customization.logo;

  const isPurchaseBookingExtension = Boolean(purchase.offers && purchase.offers[0] && mFinance.purchaseOfferTypeIsBookingExtension(purchase.offers[0]));
  const isPurchaseBookingOverstay = Boolean(purchase.offers && purchase.offers[0] && mFinance.purchaseOfferTypeIsBookingOverstay(purchase.offers[0]));

  if (isPurchaseBookingExtension || isPurchaseBookingOverstay) {
    end = purchase.offers[0].end;
  }
  /* ========================================================= */
  /* SUB COMPONENTS                                            */
  /* ========================================================= */

  /* --------------- */
  /* Purchase title  */
  /* --------------- */
  /* eslint-disable */
  const titleId =  isPurchaseBookingExtension ? 'title.booking.extendPaySpace'
                 : isPurchaseBookingOverstay ? 'title.booking.overstayPaySpace'
                 : 'title.booking.paySpace';
  /* eslint-enable */

  const PurchaseTitle = (
    <div className={`bookPageTitle ${isMobile ? 'text-center' : 'text-right'}`}>
      <LocalizedMessage id={titleId} />
    </div>
  );

  /* --------------- */
  /* Logo and title  */
  /* --------------- */
  const Logo = hasLogo ? (
    <div className="text-center mb-3">
      <img src={customization.logo} className="company-logo company-logo__purchase" alt="Company Logo" />
    </div>
  ) : null;
  const CarparkName = <div className="text-center px-3 checkoutTitle">{carparkName}</div>;

  const LogoAndTitle = (
    <div className="mb-3">
      {Logo}
      {CarparkName}
    </div>
  );

  /* -------------------- */
  /* Carpark information  */
  /* -------------------- */

  const CarparkAddress = (!isMobile && carparkAddress) ? (
    <div className="d-flex justify-content-center align-items-center my-3">
      <AppIcon id="itemAddressOutline" color={theme.palette.primary.main} />
      <div className="pl-1 infoItemLink bookCardTitle">{formatAddressForCpmClient(carparkAddress)}</div>
    </div>
  ) : null;

  /* ------------------------- */
  /* Booking information item  */
  /* ------------------------- */
  const BookingInformationItem = ({ labelId, iconId, children: childrenInformation }) => {
    const FLEX_SIZE = isMobile ? '100%' : '50%';

    return (
      <div
        className={classNames('d-flex align-items-center', { 'flex-column align-items-center': isMobile })}
        style={{
          flexBasis: FLEX_SIZE,
          flexGrow: 1,
          columnGap: '15px',
          rowGap: '5px',
        }}
      >
        <AppIcon id={iconId} color={theme.palette.primary.main} />
        <div className={classNames('d-flex flex-column', { 'align-items-center': isMobile })}>
          <Typography type="infoItemLabel">
            <LocalizedMessage id={labelId} />
          </Typography>
          <Typography type="infoItemSubtitle" component="div">
            {childrenInformation}
          </Typography>
        </div>
      </div>
    );
  };
  BookingInformationItem.propTypes = {
    labelId: PropTypes.string.isRequired,
    iconId: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
  };

  /* -------*/
  /* Begin  */
  /* ------ */

  const Begin = (
    <BookingInformationItem labelId="field.booking.startDate" iconId="itemDateBegin">
      <LocalizedStandardDateNoSeconds value={dateGenerator.date(begin)} />
    </BookingInformationItem>
  );

  /* -----*/
  /* End  */
  /* ---- */
  const endTitleId = isPurchaseBookingExtension ? 'field.booking.extendedEndDate' : 'field.booking.endDate';

  const End = (
    <BookingInformationItem labelId={endTitleId} iconId="itemDateEnd">
      <LocalizedStandardDateNoSeconds value={dateGenerator.date(end)} />
    </BookingInformationItem>
  );

  /* -------------- */
  /* License plate  */
  /* -------------- */
  const licensePlate = vehicles && vehicles[0] && vehicles[0].licensePlate;

  const LicensePlate = licensePlate ? (
    <BookingInformationItem labelId="field.vehicle" iconId="itemVehicle">
      <Typography type="infoItemSubtitle">{licensePlate}</Typography>
    </BookingInformationItem>
  ) : null;

  /* ----------------- */
  /* Multiple entries  */
  /* ----------------- */

  const MultipleEntries = (
    <BookingInformationItem labelId="field.booking.entries" iconId="itemMultipleArrow">
      <LocalizedMessage id="field.booking.typeEntry" />
    </BookingInformationItem>
  );

  /* ------ */
  /* Price  */
  /* ------ */

  const Price = (
    <div className="row mt-3 py-3">
      <PurchaseInfoPrice data={purchase} dataType="purchase" showDetails />
    </div>
  );

  /* ========================================================= */
  /* UI                                                        */
  /* ========================================================= */

  const CARD_WIDTH = '300px';
  const CARDS_SPACING = '20px';

  return (
    <div>
      {PurchaseTitle}
      <div className="d-flex align-items-start flex-wrap" style={{ gap: CARDS_SPACING }}>
        {/* 🃏 Purchase recap */}
        <Box type="tertiary" className="p-5" style={{ flexBasis: CARD_WIDTH, flexGrow: 1 }}>
          <React.Fragment>
            {LogoAndTitle}
            {CarparkAddress}
            <div className="d-flex justify-content-center flex-wrap my-4" style={{ rowGap: '15px' }}>
              {Begin}
              {End}
              {LicensePlate}
              {MultipleEntries}
            </div>
            {Price}
          </React.Fragment>
        </Box>
        {/* 🃏 Paying form */}
        <Card style={{ flexBasis: CARD_WIDTH, flexGrow: 1 }}>
          {children}
        </Card>
      </div>
    </div>
  );
};

PurchaseWrapperBooking.propTypes = {
  children: PropTypes.node.isRequired,
  customization: cpmSlugCustomizationShape,
  product: PropTypes.oneOfType([cpmMyBookingShape, cpmOnDemandAccessForCheckoutDataShape]).isRequired,
  purchase: cpmPurchaseShape.isRequired,
  theme: PropTypes.object.isRequired,
};

PurchaseWrapperBooking.defaultProps = {
  customization: null,
};

const mapStateToProps = (state) => ({
  customization: _get(state.cpmSlug.current.result, 'customization'),
});

module.exports = compose(
  withTheme,
  connect(mapStateToProps),
)(PurchaseWrapperBooking);



// WEBPACK FOOTER //
// ./routes/purchase/assets/PurchaseWrapperBooking.js